import type { GenericFormType } from '@neo/blocks/forms/types'
import { Button } from '@neo/primitives/button'
import { Checkbox } from '@neo/primitives/checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@neo/primitives/dialog'
import { Label } from '@neo/primitives/label'
import { Select } from '@neo/primitives/select'
import { Tooltip } from '@neo/primitives/tooltip'
import { Pencil, PlusCircle } from 'lucide-react'
import { useState } from 'react'
import type { Credential, IPAccessControlList } from 'v2/types/api/settings'
import {
  getManagedAclIps,
  isManagedAclPlatform,
  SIP_PLATFORMS,
  type SipPlatformKey,
} from '../platform-config'
import type { UnifiedApplicationFormValues } from '../types'

type OutboundAuthType = 'ip_access_control_list' | 'credentials' | 'both' | undefined

type AuthForm = GenericFormType<UnifiedApplicationFormValues>

/** Label row with a right-aligned "Create new …" action, per the design */
const FieldHeader = ({
  label,
  actionLabel,
  onAction,
  actionTestId,
}: {
  label: string
  actionLabel: string
  onAction: () => void
  actionTestId: string
}) => (
  <Label className="flex items-center justify-between">
    {label}
    {/* same Button treatment as "Buy new number" so every inline CTA in the
        sheet shares font, size and hover interaction (Tejal, 2026-08-03) */}
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={(e) => {
        e.preventDefault()
        onAction()
      }}
      className="text-primary h-auto p-0"
      data-testid={actionTestId}
    >
      <PlusCircle className="mr-1 h-3 w-3" />
      {actionLabel}
    </Button>
  </Label>
)

/** Pencil affordance inside a select option, opening that record for edit */
const EditOptionButton = ({ onEdit, testId }: { onEdit: () => void; testId: string }) => (
  <button
    className="absolute right-0 flex-shrink-0 p-1 hover:bg-accent rounded"
    onClick={(e) => {
      e.preventDefault()
      e.stopPropagation()
      onEdit()
    }}
    type="button"
    data-testid={testId}
  >
    <Pencil className="h-3 w-3" />
  </button>
)

/**
 * Customer-owned ACL select (Custom platform only). Writes form values
 * directly — see the card's note on why these are not mounted fields.
 */
const CustomAclSelect = ({
  form,
  ipAccessControlLists,
  onCreateNewIPACL,
  onEditIPACL,
}: {
  form: AuthForm
  ipAccessControlLists: IPAccessControlList[]
  onCreateNewIPACL: () => void
  onEditIPACL: (ipacl: IPAccessControlList) => void
}) => {
  const ipaclUuid = form.useStore((state) =>
    'ipacl_uuid' in state.values ? (state.values.ipacl_uuid as string | undefined) : undefined,
  )
  const exists = ipaclUuid
    ? ipAccessControlLists.some((ipacl) => ipacl.ipacl_uuid === ipaclUuid)
    : false

  return (
    <div className="flex flex-col gap-2">
      <FieldHeader
        label="Add an IP Access Control List"
        actionLabel="Create new ACL"
        onAction={onCreateNewIPACL}
        actionTestId="button-create-new-acl"
      />
      <Select
        options={ipAccessControlLists.map((ipacl) => ({
          label: (
            <div className="relative flex items-center w-full min-w-0">
              <span className="flex-1 truncate min-w-0 pr-8">
                {ipacl.name} ({ipacl.ip_addresses.length} IPs)
              </span>
              <EditOptionButton
                onEdit={() => onEditIPACL(ipacl)}
                testId={`button-edit-ipacl-${ipacl.ipacl_uuid}`}
              />
            </div>
          ),
          value: ipacl.ipacl_uuid,
          searchValue: `${ipacl.name} ${ipacl.ip_addresses.length}`,
        }))}
        value={exists ? (ipaclUuid as string) : ''}
        placeholder="Select IP access control list"
        onChange={(value) => {
          const newValue = Array.isArray(value) ? value[0] || '' : value
          form.setFieldValue('ipacl_uuid', newValue || undefined)
          form.validateField('trunk_authentication_type', 'change')
        }}
        selectedValueRenderer={({ value }) => {
          const stringValue = Array.isArray(value) ? value[0] || '' : String(value)
          const ipacl = ipAccessControlLists.find((i) => i.ipacl_uuid === stringValue)
          return <span>{ipacl ? `${ipacl.name} (${ipacl.ip_addresses.length} IPs)` : ''}</span>
        }}
      />
    </div>
  )
}

/** Credential select, shown for every platform once Credentials is checked */
const CredentialSelect = ({
  form,
  credentials,
  onCreateNewCredential,
  onEditCredential,
}: {
  form: AuthForm
  credentials: Credential[]
  onCreateNewCredential: () => void
  onEditCredential: (credential: Credential) => void
}) => {
  const credentialUuid = form.useStore((state) =>
    'credential_uuid' in state.values
      ? (state.values.credential_uuid as string | undefined)
      : undefined,
  )
  const exists = credentialUuid
    ? credentials.some((cred) => cred.credential_uuid === credentialUuid)
    : false

  return (
    <div className="flex flex-col gap-2">
      <FieldHeader
        label="Authentication"
        actionLabel="Create new credential"
        onAction={onCreateNewCredential}
        actionTestId="button-create-new-credential"
      />
      <Select
        options={credentials.map((cred) => ({
          label: (
            <div className="relative flex items-center w-full min-w-0">
              <span className="flex-1 truncate min-w-0 pr-8">{cred.name || cred.username}</span>
              <EditOptionButton
                onEdit={() => onEditCredential(cred)}
                testId={`button-edit-credential-${cred.credential_uuid}`}
              />
            </div>
          ),
          value: cred.credential_uuid,
          searchValue: `${cred.name || ''} ${cred.username || ''}`.trim(),
        }))}
        value={exists ? (credentialUuid as string) : ''}
        placeholder="Select authentication credential"
        onChange={(value) => {
          const newValue = Array.isArray(value) ? value[0] || '' : value
          form.setFieldValue('credential_uuid', newValue || undefined)
          form.validateField('trunk_authentication_type', 'change')
        }}
        selectedValueRenderer={({ value }) => {
          const stringValue = Array.isArray(value) ? value[0] || '' : String(value)
          const cred = credentials.find((c) => c.credential_uuid === stringValue)
          return <span>{cred?.name || cred?.username || ''}</span>
        }}
      />
    </div>
  )
}

/** Read-only Plivo-managed ACL row + its IP list dialog */
const ManagedAclDisplay = ({ platform }: { platform: SipPlatformKey }) => {
  const [aclModalOpen, setAclModalOpen] = useState(false)
  const aclName = SIP_PLATFORMS[platform].managedAclName

  return (
    <>
      <div
        className="flex items-center justify-between bg-background border rounded-md px-3 py-2"
        data-testid="managed-acl-display"
      >
        <span className="text-p-400 text-foreground">{aclName}</span>
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0"
          onClick={() => setAclModalOpen(true)}
          data-testid="button-view-managed-acl"
        >
          View
        </Button>
      </div>
      <Dialog open={aclModalOpen} onOpenChange={(open) => !open && setAclModalOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              <div className="flex flex-col gap-1">
                <span>{aclName}</span>
                <span className="text-s-400 text-secondary">Managed by Plivo · Read only</span>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label>IP addresses</Label>
            <div className="flex flex-wrap gap-2 bg-bg3 rounded-md p-3">
              {getManagedAclIps(platform).map((ip) => (
                <span
                  key={ip}
                  className="bg-background border rounded-md px-3 py-1 text-p-400 text-foreground font-jetbrains tabular-nums"
                >
                  {ip}
                </span>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

/** IP-ACL checkbox: disabled for LiveKit (unavailable) and managed (implicit) */
const AclCheckbox = ({
  platform,
  checked,
  onToggle,
}: {
  platform: SipPlatformKey
  checked: boolean
  onToggle: () => void
}) => {
  const aclMode = SIP_PLATFORMS[platform].outboundAcl

  if (aclMode === 'unavailable') {
    return (
      <Tooltip content="Not available for LiveKit outbound" side="bottom">
        <span className="w-fit cursor-not-allowed opacity-50">
          <Checkbox
            name="livekit-ipacl"
            label="IP access control list"
            value={false}
            disabled
            onChange={() => {}}
            data-testid="checkbox-livekit-ipacl"
          />
        </span>
      </Tooltip>
    )
  }
  if (aclMode === 'managed') {
    return (
      <Tooltip content="Required, cannot be deselected" side="bottom">
        <span className="w-fit cursor-not-allowed">
          <Checkbox
            name="managed-ipacl"
            label="IP access control list"
            value
            disabled
            onChange={() => {}}
            data-testid="checkbox-managed-ipacl"
          />
        </span>
      </Tooltip>
    )
  }
  return (
    <Checkbox
      name="custom-ipacl"
      label="IP access control list"
      value={checked}
      onChange={onToggle}
      data-testid="checkbox-custom-ipacl"
    />
  )
}

/**
 * Renders as the trunk_authentication_type field's component (checkbox card per
 * the design), with the ACL/credential selects nested INSIDE the card:
 * - LiveKit: no IP ACL, credentials checkbox (user-clickable, never auto-set).
 * - Retell/Vapi/ElevenLabs: Plivo-managed IP ACL, force-checked + read-only
 *   (provisioned into the account at submit), plus optional credentials.
 * - Custom: both checkboxes freely toggleable with their selects below.
 *
 * IMPORTANT: the nested selects write form values via setFieldValue and are NOT
 * mounted as form fields. Mounting a second field instance for a name that also
 * has a (hidden) edit-mode field corrupts TanStack 0.28's field registry in the
 * browser build and crashes on click. Their required/exists validation lives on
 * THIS field's zodValidation, and the error text renders below the card.
 */
export const PlatformOutboundAuthCard = ({
  form,
  ipAccessControlLists,
  credentials,
  onCreateNewIPACL,
  onEditIPACL,
  onCreateNewCredential,
  onEditCredential,
}: {
  form: AuthForm
  ipAccessControlLists: IPAccessControlList[]
  credentials: Credential[]
  onCreateNewIPACL: () => void
  onEditIPACL: (ipacl: IPAccessControlList) => void
  onCreateNewCredential: () => void
  onEditCredential: (credential: Credential) => void
}) => {
  const sipPlatform = form.useStore(
    (state) => state.values.sip_platform as SipPlatformKey | undefined,
  )
  const authType = form.useStore((state) =>
    'trunk_authentication_type' in state.values
      ? (state.values.trunk_authentication_type as OutboundAuthType)
      : undefined,
  )

  if (!sipPlatform) return null

  const isManaged = isManagedAclPlatform(sipPlatform)
  const isCustom = SIP_PLATFORMS[sipPlatform].outboundAcl === 'custom'
  // Managed platforms always authenticate by the Plivo ACL, whatever the stored
  // value says: the checkbox is force-checked and the validator treats the ACL
  // as implicit, so no auto-set is needed to keep the form satisfiable.
  const aclChecked = isManaged || authType === 'ip_access_control_list' || authType === 'both'
  const credentialsChecked = authType === 'credentials' || authType === 'both'

  const setAuthType = (next: OutboundAuthType) => {
    form.setFieldValue('trunk_authentication_type', next)
    form.validateField('trunk_authentication_type', 'change')
  }

  const toggleAcl = () => {
    if (!isCustom) return // managed = implicit, LiveKit = unavailable
    if (aclChecked) {
      // Unchecking clears the selection too, or the hidden ipacl_uuid would
      // still ride along in the create payload.
      form.setFieldValue('ipacl_uuid', undefined)
      setAuthType(credentialsChecked ? 'credentials' : undefined)
    } else {
      setAuthType(credentialsChecked ? 'both' : 'ip_access_control_list')
    }
  }

  const toggleCredentials = () => {
    const keepAcl = aclChecked && !isManaged
    if (credentialsChecked) {
      form.setFieldValue('credential_uuid', undefined)
      setAuthType(keepAcl ? 'ip_access_control_list' : undefined)
    } else {
      setAuthType(keepAcl ? 'both' : 'credentials')
    }
  }

  return (
    <div className="grid gap-2 col-span-2">
      <Label>Trunk Authentication Type</Label>
      {/* flex column, NOT grid: col-span classes on children would create an
          implicit second column in a 1-col grid and warp the layout */}
      <div className="bg-bg3 rounded-md p-3 flex flex-col gap-4">
        <AclCheckbox platform={sipPlatform} checked={aclChecked} onToggle={toggleAcl} />
        {isManaged && <ManagedAclDisplay platform={sipPlatform} />}
        {isCustom && aclChecked && (
          <CustomAclSelect
            form={form}
            ipAccessControlLists={ipAccessControlLists}
            onCreateNewIPACL={onCreateNewIPACL}
            onEditIPACL={onEditIPACL}
          />
        )}
        <Checkbox
          name="platform-credentials"
          label={
            <span>
              Credentials
              {isManaged && <span className="text-s-400 text-secondary italic"> (Optional)</span>}
            </span>
          }
          value={credentialsChecked}
          onChange={toggleCredentials}
          data-testid="checkbox-platform-credentials"
        />
        {credentialsChecked && (
          <CredentialSelect
            form={form}
            credentials={credentials}
            onCreateNewCredential={onCreateNewCredential}
            onEditCredential={onEditCredential}
          />
        )}
      </div>
    </div>
  )
}
